import * as React from 'react';
import { MATCH_LIST_BEM, MatchListItemLayout, MATCH_LIST_GROUP_BY } from '../match-list/utils/match-list.constants';
import { FilteredMarketGroupsOverview, FilteredMatchesWithEvents } from '../match-list/utils/match-list-models';
import EventItemNormal from '../event-item/event-item';
import EventItemV2 from '../event-item/event-item-v2';
import { MatchListGroupData, MATCHLIST_GROUP_BEM as bem } from './match-list-group.constants';
import MatchListGroupHeader from './match-list-group-header';
import { RouteNames } from '@om-fe/core/core/routing/utils';
import { DisciplineLiveStatus, ModelTypes } from '@om-fe/models';
import getConfig, { MarketsHeaderOptions, OMFE2ConfigModel } from '@om-fe/core/utils/config';
import { PlatformTypes } from '@om-fe/utils';
import { BonusType } from './match-list-special-header';

interface MatchListGroupState {
    readonly isExpanded: boolean;
}

interface MatchListGroupPublicProps {
    readonly activeMatchId?: string | null;
    readonly eventItemExtraClassName?: string | string[];
    readonly items: FilteredMatchesWithEvents[];
    readonly groupData: MatchListGroupData;
    readonly index: number;
    readonly isExpanded: boolean;
    readonly locationId: string | undefined;
    readonly categoryId: string | undefined;
    readonly marketsHeaderOptions: MarketsHeaderOptions;
    readonly numberOfOddsExpanded?: number;
    readonly sliceKey: string;
    readonly routeName?: RouteNames;
    readonly liveStatus: DisciplineLiveStatus;
    readonly itemLayout: MatchListItemLayout;
    readonly filteredMainMarketsList: string[];
    readonly filteredMainOutrightsMarketsList: string[];
    readonly showMarketInMatchListGroupHeader: boolean;
    readonly mainMarketsLimit: number;
    readonly activeStatistic: string;
    readonly setActiveStatistic?: (instanceId: string) => void,
    readonly groupOverview?: string;
    readonly selectGroupsOverview?: (item: string) => void;
    readonly filteredMarketGroupsOverviewList?: string[];
    readonly filteredMarketGroupsOverview?: Record<string, FilteredMarketGroupsOverview>;
    readonly isHasDrawer?: boolean;
    readonly deviceType?: PlatformTypes;
    readonly showEventCategory?: boolean;
    readonly preventRedirect?: boolean;
    readonly bonusType?: BonusType;
    readonly isVirtual?: boolean;
    readonly instanceId?: string;
}

type MatchListGroupProps = MatchListGroupPublicProps;

export default class MatchListGroup extends React.PureComponent<MatchListGroupProps, MatchListGroupState> {
    constructor(props: MatchListGroupProps) {
        super(props);
        const { activeMatchId, items, isExpanded } = this.props;
        let isActiveMatch: boolean = false;
        for (const { id } of items) {
            if (id === activeMatchId) {
                isActiveMatch = true;
                break;
            }
        }

        this.state = {
            isExpanded: isActiveMatch || isExpanded,
        };
    }

    toggleExpanded = (): void => {
        const { isExpanded } = this.state;

        this.setState({
            isExpanded: !isExpanded,
        });
    };

    getLocationItem = (items: FilteredMatchesWithEvents[]) => {
        const mutableIndexMap: Record<string, number> = {};
        const mutableGroupedMatches: FilteredMatchesWithEvents[][] = [];
        items.forEach((item): void => {
            const key: string = item[MATCH_LIST_GROUP_BY];
            const index: number | undefined = mutableIndexMap[key];
            
            if (index !== undefined) {
                mutableGroupedMatches[index].push(item);
            } else {
                mutableGroupedMatches.push([item]);
                mutableIndexMap[key] = mutableGroupedMatches.length - 1;
            }
        });

        return mutableGroupedMatches
    };


    render(): React.ReactNode {
        const {
            activeMatchId,
            eventItemExtraClassName,
            items,
            groupData,
            locationId,
            categoryId,
            marketsHeaderOptions,
            numberOfOddsExpanded,
            routeName,
            sliceKey,
            liveStatus,
            itemLayout,
            filteredMainMarketsList,
            filteredMainOutrightsMarketsList,
            showMarketInMatchListGroupHeader,
            mainMarketsLimit,
            activeStatistic,
            setActiveStatistic,
            groupOverview,
            selectGroupsOverview,
            filteredMarketGroupsOverviewList,
            filteredMarketGroupsOverview,
            isHasDrawer,
            deviceType,
            showEventCategory,
            preventRedirect,
            bonusType,
            isVirtual,
            instanceId,
        } = this.props;
        const { isExpanded } = this.state;
        const config: OMFE2ConfigModel = getConfig();
        const displayShortParentName: boolean | undefined = config.matchListConfig?.commonConfig?.displayShortParentName;
        const sortSpecialItemsBySportId: boolean = config.specialBonus?.sortItemsBySportId ?? false;

        const EventItem = (() => {
            switch (itemLayout) {
                case MatchListItemLayout.V2:
                case MatchListItemLayout.Special:
                case MatchListItemLayout.VirtualSport:
                    return EventItemV2;
                default:
                    return EventItemNormal;
            }
        })();

        const sortedItems: FilteredMatchesWithEvents[] = [...items].sort((a, b) => {
            const sportDiff = Number(a.sportId) - Number(b.sportId);
            if (sportDiff !== 0 && itemLayout === MatchListItemLayout.Special && sortSpecialItemsBySportId) {
                return sportDiff;
            }

            const aHasTime = typeof a.startTime === 'number';
            const bHasTime = typeof b.startTime === 'number';

            if (aHasTime && bHasTime) return a.startTime - b.startTime;
            if (aHasTime) return -1;
            if (bHasTime) return 1;

            return 0;
        });

        return (
            <div className={bem({
                extra: MATCH_LIST_BEM('Group'),
                modifiers: {
                    Outright: groupData._type === ModelTypes.TOURNAMENT
                }
            })}>
                {itemLayout !== MatchListItemLayout.Special && (
                    <MatchListGroupHeader
                        sliceKey={sliceKey}
                        locationId={locationId}
                        categoryId={categoryId}
                        isExpanded={isExpanded}
                        groupData={groupData}
                        options={marketsHeaderOptions}
                        toggle={this.toggleExpanded}
                        filteredMainMarketsList={filteredMainMarketsList}
                        filteredMainOutrightsMarketsList={filteredMainOutrightsMarketsList}
                        showMarketInMatchListGroupHeader={showMarketInMatchListGroupHeader}
                        mainMarketsLimit={mainMarketsLimit}
                        groupOverview={groupOverview}
                        selectGroupsOverview={selectGroupsOverview}
                        filteredMarketGroupsOverviewList={filteredMarketGroupsOverviewList}
                        filteredMarketGroupsOverview={filteredMarketGroupsOverview}
                        isHasDrawer={isHasDrawer}
                        deviceType={deviceType}
                        showEventCategory={showEventCategory}
                        preventRedirect={preventRedirect}
                        isVirtual={isVirtual}
                    />
                )}

                {isExpanded && (
                    <div className={bem('Content')}>
                        {
                            marketsHeaderOptions === MarketsHeaderOptions.SHOW_COUNTRY ?
                                this.getLocationItem(items).map((tournament, key: number) => (
                                    <>
                                        <div className={bem('Title')} key={key}>{displayShortParentName ? tournament[0].shortParentName : tournament[0].parentName}</div>
                                        {tournament.map(({ id, _type }, index: number) => (
                                            <EventItem
                                                extraClassName={eventItemExtraClassName}
                                                isActive={id === activeMatchId}
                                                isExpanded={numberOfOddsExpanded !== undefined ? index < numberOfOddsExpanded : true}
                                                isExpandable={numberOfOddsExpanded !== undefined}
                                                key={id}
                                                matchId={id}
                                                routeName={routeName}
                                                sliceKey={sliceKey}
                                                liveStatus={liveStatus}
                                                mainMarketsLimit={_type === ModelTypes.TOURNAMENT ? 1 : mainMarketsLimit}
                                                instanceId={`statistic_${id}`}
                                                isStatisticExpanded={activeStatistic === `statistic_${id}`}
                                                setActiveStatistic={setActiveStatistic}
                                                modelType={_type}
                                                itemLayout={itemLayout}
                                                bonusType={bonusType}
                                                isVirtual={isVirtual}
                                                matchListInstance={instanceId}
                                            />
                                        ))}
                                    </>
                                )) 
                            : sortedItems.map(({ id, _type }, index: number) => {
                                return (
                                    <EventItem
                                        key={id}
                                        extraClassName={eventItemExtraClassName}
                                        isActive={id === activeMatchId}
                                        isExpanded={
                                            numberOfOddsExpanded !== undefined
                                                ? index < numberOfOddsExpanded
                                                : true
                                        }
                                        isExpandable={numberOfOddsExpanded !== undefined}
                                        matchId={id}
                                        routeName={routeName}
                                        sliceKey={sliceKey}
                                        liveStatus={liveStatus}
                                        mainMarketsLimit={_type === ModelTypes.TOURNAMENT ? 1 : mainMarketsLimit}
                                        instanceId={`statistic_${id}`}
                                        isStatisticExpanded={activeStatistic === `statistic_${id}`}
                                        setActiveStatistic={setActiveStatistic}
                                        modelType={_type}
                                        itemLayout={itemLayout}
                                        bonusType={bonusType}
                                        isVirtual={isVirtual}
                                        matchListInstance={instanceId}
                                    />
                                );
                            })
                        }
                    </div>
                )}
            </div>
        );
    }
}
